import React, { useState } from 'react';
import { Circle as XCircle, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, ChevronDown } from 'lucide-react';
import { SEVERITY_CONFIG } from '../../types/quant-audit';
import { Badge } from '../ui/badge';

const ICONS = {
  XCircle,
  AlertTriangle,
  CheckCircle2,
};

export default function RuleCard({ violation }) {
  const [expanded, setExpanded] = useState(false);
  const config = SEVERITY_CONFIG[violation.severity] || SEVERITY_CONFIG.warning;
  const Icon = ICONS[config.icon] || AlertTriangle;

  const borderColorMap = {
    fail: '#ff3b30',
    warning: '#f5b400',
    pass: '#00f5d4',
  };

  return (
    <div
      className={`wk-rule-card ${config.bg} ${config.glow} cursor-pointer`}
      style={{ borderLeftColor: borderColorMap[violation.severity] }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`mt-0.5 flex-shrink-0 ${config.text}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-[10px] font-mono font-bold text-[#8b93a7]">{violation.rule_id}</span>
                <Badge className={`text-[9px] px-1.5 py-0 ${
                  violation.severity === 'fail' ? 'wk-badge-fail' :
                  violation.severity === 'warning' ? 'wk-badge-warn' : 'wk-badge-pass'
                }`}>
                  {config.label}
                </Badge>
                {violation.category && (
                  <span className="text-[9px] text-[#8b93a7] font-mono">{violation.category}</span>
                )}
              </div>
              <h4 className="text-sm font-semibold text-white mb-1">{violation.rule_name}</h4>
              <p className="text-xs text-[#8b93a7] leading-relaxed">{violation.summary}</p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#8b93a7] flex-shrink-0 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      <div className={`wk-rule-card-content ${expanded ? 'expanded' : ''}`} style={{ transition: 'grid-template-rows 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
        <div>
          <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-3">
            <p className="text-xs text-[#d8dae0] leading-relaxed font-mono">
              {violation.detail_explanation}
            </p>

            {violation.affected_waves?.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-[#8b93a7] uppercase tracking-wider">Affected:</span>
                {violation.affected_waves.map((w) => (
                  <span key={w} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white">
                    {w}
                  </span>
                ))}
              </div>
            )}

            {violation.fib_values?.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] text-[#8b93a7] uppercase tracking-wider">Fibonacci:</span>
                {violation.fib_values.map((fv) => (
                  <div key={fv.label} className="flex items-center gap-1.5 text-[11px] font-mono">
                    <span className="text-[#8b93a7]">{fv.label}:</span>
                    <span className={`font-bold ${
                      fv.value > 1 ? 'text-[#ff3b30]' : fv.value > 0.786 ? 'text-[#f5b400]' : 'text-[#00f5d4]'
                    }`}>
                      {fv.value.toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
